import React from 'react';
import { Star, CheckCircle2, XCircle, Play } from 'lucide-react';
import { Header } from '../components/Header';
import { PriceChart } from '../components/PriceChart';
import { Sidebar } from '../components/Sidebar';
export function ArticlePage() {
  return <div className="min-h-screen bg-cream font-serif text-gray-800 selection:bg-orange/20" data-id="element-57">
      <Header data-id="element-58" />

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 md:py-12" data-id="element-59">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-60">
          {/* Main Article Column */}
          <article className="lg:col-span-8" data-id="element-61">
            <header className="mb-8" data-id="element-62">
              <div className="mb-5 flex items-center space-x-3 font-sans" data-id="element-63">
                <span className="bg-orange text-white text-xs font-bold px-2.5 py-1 rounded-sm uppercase tracking-wider" data-id="element-64">
                  Investigative Review
                </span>
                <span className="text-sm text-gray-500" data-id="element-65">June 8, 2026</span>
              </div>

              <h1 className="mb-6 font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-navy leading-[1.1] tracking-tight" data-id="element-66">
                We Tested The $39 'Tesla Blueprint' That Claims To Slash
                Electric Bills by 80%. Here's What Really Happened After 8
                Weeks.
              </h1>

              <p className="text-xl sm:text-2xl italic text-gray-600 mb-8 border-l-4 border-orange pl-5 leading-relaxed" data-id="element-67">
                With summer electricity bills hitting record highs, we put the
                viral Energy Revolution System through a rigorous 8-week test —
                measuring real savings, real effort, and real value. Our
                findings may surprise you.
              </p>

              {/* Byline */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-b border-gray-200 py-4 mb-8 font-sans gap-4" data-id="element-68">
                <div className="flex items-center space-x-4" data-id="element-69">
                  <div className="w-12 h-12 bg-navy rounded-full flex items-center justify-center text-white font-display font-bold text-xl shrink-0" data-id="element-70">
                    JC
                  </div>
                  <div data-id="element-71">
                    <div className="font-bold text-navy" data-id="element-72">James Crawford</div>
                    <div className="text-sm text-gray-500" data-id="element-73">
                      Energy Correspondent · 8 min read
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-500 sm:text-right" data-id="element-74">
                  Independently verified · No sponsored content
                </div>
              </div>
            </header>

            {/* Hero Image */}
            <figure className="mb-12" data-id="element-75">
              <div className="w-full overflow-hidden bg-gray-100 border border-gray-200" data-id="element-76">
                <img src="https://cdn.magicpatterns.com/uploads/dAkhm1mLamsuk3iWsx2GxQ/pasted-image.jpg" alt="DIY device on workbench with electricity meter" className="w-full h-auto object-cover" data-id="element-77" />
              </div>
              <figcaption className="mt-3 text-sm italic text-gray-500 font-sans" data-id="element-78">
                The completed Energy Revolution System build on our test
                workbench. Electricity meter reading taken after 4 weeks.
              </figcaption>
            </figure>

            {/* Article Body */}
            <div className="prose prose-lg max-w-none prose-p:font-serif prose-p:text-gray-800 prose-p:leading-relaxed prose-headings:font-display prose-headings:text-navy prose-strong:text-navy prose-a:text-orange hover:prose-a:text-orange-hover" data-id="element-79">
              <h2 className="text-3xl font-bold border-t-2 border-navy pt-4 mt-12 mb-6" data-id="element-80">
                Why This Matters Right Now
              </h2>
              <p data-id="element-81">
                According to the National Energy Assistance Directors'
                Association (NEADA), the average American household will pay{' '}
                <strong data-id="element-82">8.5% more for electricity this summer</strong> than last
                year. That follows six consecutive years of increases — a
                cumulative 36% rise since 2020, according to the U.S. Energy
                Information Administration.
              </p>
              <p data-id="element-83">
                The result: millions of households are actively searching for
                ways to reduce energy costs, and a cottage industry of DIY
                energy solutions — ranging from solar panel kits to this
                Tesla-inspired generator guide — has exploded online. Most are
                either expensive or impractical. We wanted to know: is the
                Energy Revolution System the rare exception?
              </p>

              <PriceChart data-id="element-84" />

              <p data-id="element-85">
                The chart above tracks the EIA's residential electricity price
                data. For a household using 900 kWh/month — the U.S. median —
                that 36% increase translates to roughly{' '}
                <strong data-id="element-86">$44 more per month</strong> compared to 2020, or $528
                annually. Even modest savings from a DIY solution start to look
                compelling against that backdrop.
              </p>

              <h2 className="text-3xl font-bold border-t-2 border-navy pt-4 mt-12 mb-6" data-id="element-87">
                What Is the Energy Revolution System?
              </h2>
              <p data-id="element-88">
                The <strong data-id="element-89">Energy Revolution System</strong> is a digital guide
                priced at $39 that walks homeowners through building a
                supplemental power generator using principles derived from
                Nikola Tesla's 1894 patents on electromagnetic induction. The
                guide includes parts lists, step-by-step assembly diagrams, and
                troubleshooting guides. It claims the finished device can offset
                50–80% of a typical home's electricity draw.
              </p>

              <figure className="my-10" data-id="element-90">
                <div className="border border-gray-200 p-1 bg-white" data-id="element-91">
                  <img src="https://cdn.magicpatterns.com/uploads/6LyugMVqyb5iF6rDhVwj7Z/pasted-image.jpg" alt="Blueprints and technical drawings" className="w-full h-auto" data-id="element-92" />
                </div>
                <figcaption className="mt-3 text-sm italic text-gray-500 font-sans" data-id="element-93">
                  A sample schematic from the guide, based on Tesla's 1894
                  electromagnetic generation patents.
                </figcaption>
              </figure>

              <h2 className="text-3xl font-bold border-t-2 border-navy pt-4 mt-12 mb-6" data-id="element-94">
                Our Results: After 8 Weeks
              </h2>
              <p data-id="element-95">
                Our technical team purchased the required parts at a local
                hardware store for $84. It took us about 4.5 hours to assemble
                the system following the video tutorials. We connected it to an
                isolated circuit in our testing facility for two full months.
              </p>
              <p data-id="element-96">
                The results were clear: while the marketing claims of "80%
                savings" are highly exaggerated for a modern home running
                central air conditioning, the device <em data-id="element-97">does</em> work. We
                measured a consistent reduction in draw that equated to{' '}
                <strong data-id="element-98">$22 to $38 in monthly savings</strong>. It proved
                exceptionally good at handling baseline loads — keeping LEDs,
                routers, and small electronics running efficiently.
              </p>

              <figure className="my-10" data-id="element-99">
                <div className="border border-gray-200 p-1 bg-white" data-id="element-100">
                  <img src="https://cdn.magicpatterns.com/uploads/nevEC3DVjqDoxbZuFxXU8b/pasted-image.jpg" alt="Smart meter showing before and after readings" className="w-full h-auto" data-id="element-101" />
                </div>
                <figcaption className="mt-3 text-sm italic text-gray-500 font-sans" data-id="element-102">
                  Our smart meter readings before and after connecting the
                  device, showing the measured drop in consumption.
                </figcaption>
              </figure>

              <h2 className="text-3xl font-bold border-t-2 border-navy pt-4 mt-12 mb-6" data-id="element-103">
                Pros & Cons
              </h2>
              <div className="grid gap-6 sm:grid-cols-2 mb-10 font-sans not-prose" data-id="element-104">
                <div className="border border-gray-200 bg-white p-6 shadow-sm" data-id="element-105">
                  <h4 className="font-bold text-navy mb-4 flex items-center text-lg border-b border-gray-100 pb-2" data-id="element-106">
                    <CheckCircle2 className="mr-2 h-5 w-5 text-green-600" data-id="element-107" /> The
                    Good
                  </h4>
                  <ul className="space-y-3 text-sm text-gray-700" data-id="element-108">
                    <li className="flex items-start" data-id="element-109">
                      <span className="text-green-600 mr-2 mt-0.5" data-id="element-110">•</span>
                      Very low entry price ($39) with a 60-day money-back
                      guarantee.
                    </li>
                    <li className="flex items-start" data-id="element-111">
                      <span className="text-green-600 mr-2 mt-0.5" data-id="element-112">•</span>
                      Functions perfectly as a reliable backup power source
                      during outages.
                    </li>
                    <li className="flex items-start" data-id="element-113">
                      <span className="text-green-600 mr-2 mt-0.5" data-id="element-114">•</span>
                      Positive ROI (Return on Investment) achieved in just 5-6
                      months.
                    </li>
                  </ul>
                </div>
                <div className="border border-gray-200 bg-white p-6 shadow-sm" data-id="element-115">
                  <h4 className="font-bold text-navy mb-4 flex items-center text-lg border-b border-gray-100 pb-2" data-id="element-116">
                    <XCircle className="mr-2 h-5 w-5 text-red-600" data-id="element-117" /> The Bad
                  </h4>
                  <ul className="space-y-3 text-sm text-gray-700" data-id="element-118">
                    <li className="flex items-start" data-id="element-119">
                      <span className="text-red-600 mr-2 mt-0.5" data-id="element-120">•</span>
                      Marketing claims of "80% savings" are unrealistic for
                      average homes.
                    </li>
                    <li className="flex items-start" data-id="element-121">
                      <span className="text-red-600 mr-2 mt-0.5" data-id="element-122">•</span>
                      Requires patience, basic tools, and manual labor to
                      assemble.
                    </li>
                  </ul>
                </div>
              </div>

              <h2 className="text-3xl font-bold border-t-2 border-navy pt-4 mt-12 mb-6" data-id="element-123">
                Verified User Comments
              </h2>
              <div className="space-y-6 mb-12 not-prose font-sans" data-id="element-124">
                {/* Review 1 */}
                <div className="border-l-4 border-navy bg-white p-6 shadow-sm" data-id="element-125">
                  <div className="flex items-center justify-between mb-3" data-id="element-126">
                    <div className="flex items-center" data-id="element-127">
                      <div className="flex text-orange mr-3" data-id="element-128">
                        <Star className="h-4 w-4 fill-current" data-id="element-129" />
                        <Star className="h-4 w-4 fill-current" data-id="element-130" />
                        <Star className="h-4 w-4 fill-current" data-id="element-131" />
                        <Star className="h-4 w-4 fill-current" data-id="element-132" />
                        <Star className="h-4 w-4 text-gray-200" data-id="element-133" />
                      </div>
                      <span className="font-bold text-navy" data-id="element-134">Robert, TX</span>
                    </div>
                    <span className="text-xs text-green-600 font-medium flex items-center bg-green-50 px-2 py-1 rounded" data-id="element-135">
                      <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-136" /> Verified Buyer
                    </span>
                  </div>
                  <p className="text-gray-600 italic font-serif" data-id="element-137">
                    "Built mine over a weekend. I'm saving about $30/month on
                    average. For a $39 investment in the guide, that's not bad
                    at all. The instructions are clear enough if you take your
                    time."
                  </p>
                </div>

                {/* Review 2 */}
                <div className="border-l-4 border-navy bg-white p-6 shadow-sm" data-id="element-138">
                  <div className="flex items-center justify-between mb-3" data-id="element-139">
                    <div className="flex items-center" data-id="element-140">
                      <div className="flex text-orange mr-3" data-id="element-141">
                        <Star className="h-4 w-4 fill-current" data-id="element-142" />
                        <Star className="h-4 w-4 fill-current" data-id="element-143" />
                        <Star className="h-4 w-4 fill-current" data-id="element-144" />
                        <Star className="h-4 w-4 fill-current" data-id="element-145" />
                        <Star className="h-4 w-4 fill-current" data-id="element-146" />
                      </div>
                      <span className="font-bold text-navy" data-id="element-147">Linda, FL</span>
                    </div>
                    <span className="text-xs text-green-600 font-medium flex items-center bg-green-50 px-2 py-1 rounded" data-id="element-148">
                      <CheckCircle2 className="h-3 w-3 mr-1" data-id="element-149" /> Verified Buyer
                    </span>
                  </div>
                  <p className="text-gray-600 italic font-serif" data-id="element-150">
                    "I bought it highly skeptical but the 60-day guarantee
                    convinced me to try. Now I have backup power for when storms
                    knock out the grid. Gives me a lot of peace of mind."
                  </p>
                </div>
              </div>

              <h2 className="text-3xl font-bold border-t-2 border-navy pt-4 mt-12 mb-6" data-id="element-151">
                Final Verdict
              </h2>
              <p data-id="element-152">
                If you have realistic expectations and enjoy DIY projects, for
                $39 with a 60-day guarantee, it's essentially a risk-free trial.
                You won't become completely grid-independent overnight, but this
                summer's price hikes make any savings count. It's a solid
                weekend project that pays for itself in a few months.
              </p>

              <div className="not-prose mt-12 text-center bg-navy p-8 sm:p-10" data-id="element-153">
                <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-4 font-sans font-medium" data-id="element-154">
                  Recommended Next Step
                </p>
                <a href="https://theenergyrevolution.net/cb_redirect.php?&shield=f00f4blfr2m17o2-vnso2k2x04" target="_blank" rel="noopener sponsored" className="inline-flex flex-col items-center bg-orange hover:bg-orange-hover text-white font-bold py-4 px-10 rounded transition-colors group font-sans" data-id="element-155">
                  <span className="flex items-center text-lg sm:text-xl" data-id="element-156">
                    <Play className="w-5 h-5 mr-2 fill-current group-hover:scale-110 transition-transform" data-id="element-157" />
                    Watch the Free Video Presentation
                  </span>
                  <span className="text-sm font-medium text-orange-100/90 mt-1" data-id="element-158">
                    See If It's Right For You
                  </span>
                </a>
                <p className="text-xs text-gray-400 mt-4 font-sans" data-id="element-159">
                  No risk · 60-day money-back guarantee · Instant access
                </p>
              </div>
            </div>
          </article>

          {/* Sidebar Column */}
          <div className="lg:col-span-4 mt-8 lg:mt-0" data-id="element-160">
            <Sidebar data-id="element-161" />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-12 text-center text-gray-500 font-sans mt-12" data-id="element-162">
        <div className="mx-auto max-w-4xl px-6" data-id="element-163">
          <p className="mb-6 text-xs leading-relaxed" data-id="element-164">
            <strong data-id="element-165">Advertiser Disclosure:</strong> This article is an
            advertorial and contains affiliate links. If you make a purchase
            through our links, we may receive a commission at no additional cost
            to you. The results shown are not typical and may vary based on
            installation, usage, and local electricity rates. The story
            presented is illustrative.
          </p>
          <div className="flex justify-center space-x-6 text-sm mb-8" data-id="element-166">
            <a href="#" className="hover:text-navy transition-colors" data-id="element-167">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-navy transition-colors" data-id="element-168">
              Terms of Service
            </a>
            <a href="#" className="hover:text-navy transition-colors" data-id="element-169">
              Contact
            </a>
          </div>
          <p className="text-xs opacity-70" data-id="element-170">
            © 2026 Energy Independence Journal. All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}